import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BODY, CROWN, LOWER_LEFT, LOWER_RIGHT, MIRROR, SUN, type Shape } from './markDrawing';

/**
 * `grow`: the mark grows from its lower leaves up to the sun, holds, and falls
 * (design 12b), while the app opens. `circle`: the figure and sun stand while
 * the leaves pass round them (design 12a), while a page loads.
 */
export type Motion = 'grow' | 'circle';

/** `@keyframes` from moments in seconds of a cycle; a step held over several moments lists them all. */
function keyframes(name: string, cycle: number, steps: [moments: number[], rule: string][]): string {
  const at = (seconds: number) => `${+((seconds / cycle) * 100).toFixed(2)}%`;
  return `@keyframes ${name}{${steps.map(([moments, rule]) => `${moments.map(at).join(',')}{${rule}}`).join('')}}`;
}

const EASE_GROW = 'animation-timing-function:cubic-bezier(.2,.9,.3,1)';
const EASE_FALL = 'animation-timing-function:cubic-bezier(.5,0,.9,.5)';

const CIRCLE_PACE = 0.65;
const CIRCLE_CYCLE = 3.4 * CIRCLE_PACE;
const CIRCLE_DELAYS = {
  lowerLeft: [0, 0.06],
  lowerRight: [1.2, 1.14],
  crown: [0.14, 0.26, 0.37, 0.49, 0.6, 0.71, 0.83, 0.94, 1.06, 0.3, 0.47, 0.64, 0.81, 0.98],
};

const CIRCLE_STYLE = [
  `.mlp-circle-part{transform-box:fill-box;transform-origin:center;animation:mlp-circle ${CIRCLE_CYCLE}s infinite both}`,
  '@keyframes mlp-circle{0%{opacity:0;transform:scale(0);animation-timing-function:cubic-bezier(.3,1.5,.5,1)}7.35%{opacity:1;transform:scale(1)}51.5%{opacity:1;transform:scale(1);animation-timing-function:cubic-bezier(.5,-0.5,.7,0)}58.8%,100%{opacity:0;transform:scale(0)}}',
  '@media (prefers-reduced-motion:reduce){.mlp-circle *{animation:none!important}}',
].join('');

// 12b is drawn on a 3.6 s cycle in which the sun is up at 1.35 s. The growth
// runs at GROWTH_PACE of that speed; the hold and the fall keep the drawn speed.
const GROW_DESIGN_CYCLE = 3.6;
const GROWN = 1.35;
const GROWTH_PACE = 0.6;
const paced = (seconds: number) => (seconds <= GROWN ? seconds * GROWTH_PACE : seconds - GROWN * (1 - GROWTH_PACE));
const drawnAt = (percent: number) => paced((percent / 100) * GROW_DESIGN_CYCLE);
const GROW_CYCLE = paced(GROW_DESIGN_CYCLE);

const GROW_LEAF_DELAYS = [0.4, 0.46, 0.52, 0.59, 0.65, 0.59, 0.52, 0.46, 0.4, 0.36, 0.46, 0.55, 0.46, 0.36];
const MEAN_LEAF_DELAY = GROW_LEAF_DELAYS.reduce((sum, delay) => sum + delay, 0) / GROW_LEAF_DELAYS.length;
/** A crown leaf runs its own clock from its delay; the pace bends at a time that depends on it, so take the mean. */
const leafAt = (percent: number) =>
  paced(MEAN_LEAF_DELAY + (percent / 100) * GROW_DESIGN_CYCLE) - paced(MEAN_LEAF_DELAY);

const GROW_STYLE = [
  '.mlp-grow-part{transform-box:fill-box;transform-origin:center}',
  `.mlp-grow-lower{transform-origin:100px 188px;animation:mlp-grow-lower ${GROW_CYCLE}s infinite both}`,
  keyframes('mlp-grow-lower', GROW_CYCLE, [
    [[0], `opacity:0;transform:translateY(0) scale(0,0);${EASE_GROW}`],
    [[drawnAt(15.3)], 'opacity:1;transform:translateY(0) scale(1,1)'],
    [[drawnAt(55.6)], `opacity:1;transform:translateY(0) scale(1,1);${EASE_FALL}`],
    [[drawnAt(65.3), GROW_CYCLE], 'opacity:0;transform:translateY(30px) scale(1,1)'],
  ]),
  `.mlp-grow-body{transform-origin:100px 180px;animation:mlp-grow-body ${GROW_CYCLE}s infinite both}`,
  keyframes('mlp-grow-body', GROW_CYCLE, [
    [[0, drawnAt(2.8)], `opacity:0;transform:translateY(0) scale(0,0);${EASE_GROW}`],
    [[drawnAt(16.7)], 'opacity:1;transform:translateY(0) scale(1,1)'],
    [[drawnAt(61.1)], `opacity:1;transform:translateY(0) scale(1,1);${EASE_FALL}`],
    [[drawnAt(70.8), GROW_CYCLE], 'opacity:0;transform:translateY(50px) scale(1,1)'],
  ]),
  `.mlp-grow-sun{animation:mlp-grow-sun ${GROW_CYCLE}s infinite both}`,
  keyframes('mlp-grow-sun', GROW_CYCLE, [
    [[0, drawnAt(25)], 'opacity:0;transform:translateY(16px);animation-timing-function:ease-out'],
    [[drawnAt(37.5)], 'opacity:1;transform:translateY(0)'],
    [[drawnAt(66.7)], `opacity:1;transform:translateY(0);${EASE_FALL}`],
    [[drawnAt(76.4), GROW_CYCLE], 'opacity:0;transform:translateY(50px)'],
  ]),
  `.mlp-grow-leaf{animation:mlp-grow-leaf ${GROW_CYCLE}s infinite both}`,
  keyframes('mlp-grow-leaf', GROW_CYCLE, [
    [[0], `opacity:0;transform:translateY(0) rotate(0) scale(0);${EASE_GROW}`],
    [[leafAt(8)], 'opacity:1;transform:translateY(0) rotate(0) scale(1)'],
    [[leafAt(66.7)], `opacity:1;transform:translateY(0) rotate(0) scale(1);${EASE_FALL}`],
    [[leafAt(76.4), GROW_CYCLE], 'opacity:0;transform:translateY(50px) rotate(25deg) scale(.9)'],
  ]),
  '@media (prefers-reduced-motion:reduce){.mlp-grow *{animation:none!important}}',
].join('');

const HANDOVER_MS = 500;
const clocks: Partial<Record<Motion, { origin: number; showing: number; lastSeen: number }>> = {};

/**
 * Milliseconds the motion has been running. A loader that takes over from one
 * still on screen, or gone a moment ago, carries on its clock instead of
 * starting over: the start mounts a new one at each of its steps.
 */
function useRunningTime(motion: Motion): number {
  const [elapsed] = useState(() => {
    const now = globalThis.performance.now();
    const clock = clocks[motion];
    if (clock && (clock.showing > 0 || now - clock.lastSeen < HANDOVER_MS)) return now - clock.origin;
    clocks[motion] = { origin: now, showing: 0, lastSeen: now };
    return 0;
  });
  useEffect(() => {
    const clock = clocks[motion];
    if (!clock) return undefined;
    clock.showing += 1;
    return () => {
      clock.showing -= 1;
      clock.lastSeen = globalThis.performance.now();
    };
  }, [motion]);
  return elapsed;
}

type Delay = (designed: number) => { animationDelay: string };

const path = ({ d, fill, transform }: Shape, key?: number) => (
  <path key={key} d={d} fill={fill} transform={transform} />
);
const sun = <circle cx={SUN.cx} cy={SUN.cy} r={SUN.r} fill={SUN.fill} />;

function Circling({ delay }: { delay: Delay }) {
  const part = (shape: Shape, designed: number, key?: number) => (
    <g key={key} className="mlp-circle-part" style={delay(designed * CIRCLE_PACE)}>
      {path(shape)}
    </g>
  );
  return (
    <>
      {path(BODY)}
      {sun}
      {LOWER_LEFT.map((shape, i) => part(shape, CIRCLE_DELAYS.lowerLeft[i], i))}
      <g transform={MIRROR}>{LOWER_RIGHT.map((shape, i) => part(shape, CIRCLE_DELAYS.lowerRight[i], i))}</g>
      {CROWN.map((shape, i) => part(shape, CIRCLE_DELAYS.crown[i], i))}
    </>
  );
}

function Growing({ delay }: { delay: Delay }) {
  return (
    <>
      <g className="mlp-grow-lower" style={delay(0)}>
        {LOWER_LEFT.map(path)}
        <g transform={MIRROR}>{LOWER_RIGHT.map(path)}</g>
      </g>
      <g className="mlp-grow-body" style={delay(0)}>
        {path(BODY)}
      </g>
      {CROWN.map((shape, i) => (
        <g key={i} className="mlp-grow-part mlp-grow-leaf" style={delay(paced(GROW_LEAF_DELAYS[i]))}>
          {path(shape)}
        </g>
      ))}
      <g className="mlp-grow-sun" style={delay(0)}>
        {sun}
      </g>
    </>
  );
}

/** In place of the whole app, of a page, or of a section or card. */
const SIZES = { start: 112, page: 64, section: 40 };

/**
 * One of MLP's two loaders, which stands still as the whole mark when the
 * device asks for less motion. `label` names what it stands in for, where
 * "Loading" alone would not say.
 */
export function MarkLoader({
  motion = 'circle',
  size = 'section',
  label,
}: {
  motion?: Motion;
  size?: keyof typeof SIZES;
  label?: string;
}) {
  const { t } = useTranslation();
  const elapsed = useRunningTime(motion);
  const delay: Delay = (designed) => ({ animationDelay: `${+(designed - elapsed / 1000).toFixed(3)}s` });
  return (
    <svg
      className={`mlp-${motion}`}
      viewBox="0 0 200 200"
      width={SIZES[size]}
      height={SIZES[size]}
      role="progressbar"
      aria-label={label ?? t('brand.loading')}
      data-motion={motion}
      style={{ display: 'block', flexShrink: 0 }}
    >
      <style>{motion === 'grow' ? GROW_STYLE : CIRCLE_STYLE}</style>
      {motion === 'grow' ? <Growing delay={delay} /> : <Circling delay={delay} />}
    </svg>
  );
}
